<svelte:options tag="app-nav-rail" />

<div id="sidemenu" bind:this={sidemenuEl}>
  <div id="backdrop" class:open />

  <div class="scroll-indicator up visible" class:open on:click={() => scrollMenu('up')}>
    <svg xmlns="http://www.w3.org/2000/svg" width="14px" height="14px" viewBox="0 0 256 512">
      <path
        fill="currentColor"
        d="M17.525 36.465l-7.071 7.07c-4.686 4.686-4.686 12.284 0 16.971L205.947 256 10.454 451.494c-4.686 4.686-4.686
        12.284 0 16.971l7.071 7.07c4.686 4.686 12.284 4.686 16.97 0l211.051-211.05c4.686-4.686 4.686-12.284
        0-16.971L34.495 36.465c-4.686-4.687-12.284-4.687-16.97 0z"
      />
    </svg>
  </div>

  <div id="tooltip" bind:this={tooltipEl}>{hovering}</div>

  <div id="menu" class:open bind:this={menuEl}>
    {#if parsedMenu.main}
      {#each parsedMenu.main as item, i}
        <menu-item
          class:open
          menu={parsedMenu.main}
          {item}
          {active}
          select={selectHandler}
          style="--el-height: 60px;"
          on:mouseover={() => !open && setHover(item.title, true)}
          on:mouseleave={clearHover}
        />
      {/each}
    {/if}
  </div>

  <div class="scroll-indicator down visible" class:open on:click={() => scrollMenu('down')}>
    <svg xmlns="http://www.w3.org/2000/svg" width="14px" height="14px" viewBox="0 0 256 512">
      <path
        fill="currentColor"
        d="M17.525 36.465l-7.071 7.07c-4.686 4.686-4.686 12.284 0 16.971L205.947 256 10.454 451.494c-4.686 4.686-4.686
        12.284 0 16.971l7.071 7.07c4.686 4.686 12.284 4.686 16.97 0l211.051-211.05c4.686-4.686 4.686-12.284
        0-16.971L34.495 36.465c-4.686-4.687-12.284-4.687-16.97 0z"
      />
    </svg>
  </div>

  <div id="footer" class:open>
    {#if parsedMenu.footer}
      {#each parsedMenu.footer as item, i}
        <menu-item
          class:open
          menu={parsedMenu.footer}
          {item}
          {active}
          select={selectHandler}
          footer={true}
          style="--el-height: 60px;"
          on:mouseover={() => !open && setHover(item.title, false)}
          on:mouseleave={clearHover}
        />
      {/each}
    {/if}
  </div>
</div>

<script>
  import { onMount, tick } from 'svelte'
  import { get_current_component } from 'svelte/internal'

  export let menu = '{}'
  export let open = false
  export let active

  $: parsedMenu = JSON.parse(menu)
  $: hovering = null
  $: topLevelItems = []
  $: sidemenuEl = null
  $: menuEl = null

  const component = get_current_component()

  let scrollUpIndicator, scrollDownIndicator, tooltipEl

  $: initMenu = Object.entries(JSON.parse(menu)).reduce((acc, kv) => {
    const [k, v] = kv
    acc[k] = (Array.isArray(v) && v.map(i => ({ ...i, id: i.id || i.title, parent: i.parent || null }))) || v
    return acc
  }, {})

  $: parsedMenu &&
    (async () => {
      if (Object.keys(parsedMenu).length === 0) return

      parsedMenu.main.forEach(i => {
        if (!i.id) {
          i.id = i.title
        }
        const ii = initMenu.main.find(ii => ii.id === i.id)
        i.title = ii.title
        i.subtitle = ii.subtitle
      })
      parsedMenu.footer.forEach(i => {
        if (!i.id) {
          i.id = i.title
        }
        const ii = initMenu.footer.find(ii => ii.id === i.id)
        i.title = ii.title
        i.subtitle = ii.subtitle
      })
      topLevelItems = parsedMenu.main.filter(item => item.parent === null)

      // have to check menu element's actual size here as the menu itself is not parsed yet on mount
      // needs await(tick) for checking the size after the menu is parsed
      await tick
      if (menuEl.scrollHeight > menuEl.offsetHeight) {
        scrollDownIndicator.classList.add('visible')
      }
    })()

  onMount(() => {
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)

    window.addEventListener('resize', () => {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    })

    // setup event listener for main menu scroll
    menuEl.addEventListener('scroll', menuScroll)
    scrollUpIndicator = sidemenuEl.querySelector('.up')
    scrollDownIndicator = sidemenuEl.querySelector('.down')
    // seems a bit backwards thinking but for some reason if visible class is not present
    // on first paint, svelte won't be able to toggle it effectively later on??
    // most likely some scss weirdness which I'm not gonna figure out right now
    // so going with this instead
    scrollUpIndicator.classList.toggle('visible')
    scrollDownIndicator.classList.toggle('visible')
    tooltipEl.style.display = 'none'
  })

  const selectHandler = selected => {
    active = selected.id

    const event = new CustomEvent('app-nav-rail-select', {
      detail: {
        selected,
      },
      bubbles: true,
      cancelable: true,
      composed: true,
    })
    component.dispatchEvent(event)
  }

  const menuScroll = e => {
    const { offsetHeight, scrollHeight, scrollTop } = menuEl

    // hide down indicator if reached end of menu
    // return early if visible class removed
    if (offsetHeight + scrollTop >= scrollHeight) {
      scrollDownIndicator.classList.remove('visible')
      return
    }

    // hide up indicator if reached back top
    // return early if visible class removed
    if (scrollTop === 0) {
      scrollUpIndicator.classList.remove('visible')
      return
    }

    // if not at bottom or top, show indicators
    scrollUpIndicator.classList.add('visible')
    scrollDownIndicator.classList.add('visible')
  }

  const setHover = (item, isInMain) => {
    hovering = item
    isInMain ? moveMenuTooltip() : moveFooterTooltip()
    tooltipEl.style.display = 'flex'
  }

  const moveMenuTooltip = () => {
    const currentItem = parsedMenu.main.find(x => x.title === hovering)
    const currInd = parsedMenu.main.indexOf(currentItem)
    tooltipEl.style.removeProperty('bottom')
    tooltipEl.style.top = `${(currInd + 1) * 60 - menuEl.scrollTop + 15}px`
  }

  const moveFooterTooltip = () => {
    const currentItem = parsedMenu.footer.find(x => x.title === hovering)
    const currInd = parsedMenu.footer.indexOf(currentItem)
    tooltipEl.style.removeProperty('top')
    tooltipEl.style.bottom = `${(parsedMenu.footer.length - currInd - 1) * 60 + 22}px`
  }

  const clearHover = () => {
    hovering = null
    tooltipEl.style.display = 'none'
  }

  const scrollMenu = direction => {
    const { scrollTop, offsetHeight, scrollHeight } = menuEl
    const scrollAmount = 120
    if (direction === 'down') {
      if (scrollTop + offsetHeight + scrollAmount < scrollHeight) {
        // if there is enough space to scroll down the full scrollAmount
        menuEl.scrollTop += 120
      } else {
        // if we can't scroll the full scrollAmount i.e. we're close to the end of menuEl
        menuEl.scrollTop = offsetHeight + scrollHeight
      }
    } else if (direction === 'up') {
      if (scrollTop - scrollAmount > 0) {
        // if there is enough space to scroll up the full scrollAmount
        menuEl.scrollTop -= 120
      } else {
        // if we can't scroll the full scrollAmount i.e. we're close to the beginning of menuEl
        menuEl.scrollTop = 0
      }
    }
  }
</script>

<style type="text/scss">
  ::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }

  #sidemenu {
    position: fixed;
    width: 300px;
    top: 70px;
    left: 0px;
    height: calc(var(--vh, 1vh) * 100 - 70px);
    user-select: none;
    display: flex;
    flex-direction: column;
    z-index: 1000;
    pointer-events: none;
    @media print {
      display: none;
    }

    .scroll-indicator {
      width: 70px;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1;
      color: var(--ripe-app-navbar-background, #080037);
      transition: width 0.2s ease-in-out;
      flex: 0.1;
      max-height: 16px;
      pointer-events: initial;
      cursor: pointer;

      &.open {
        width: 300px;
      }

      &.up > svg {
        transform: rotate(-90deg);
      }

      &.down > svg {
        transform: rotate(90deg);
      }

      &.visible {
        color: var(--ripe-app-navbar-indicator, #f59331);
        &:hover {
          background: var(--ripe-app-primary-80, #3d4771);
        }
      }
    }

    #backdrop {
      position: fixed;
      width: 300px;
      top: 70px;
      left: -230px;
      background-color: var(--ripe-app-navbar-background, #080037);
      height: calc(var(--vh, 1vh) * 100 - 70px);
      transition: left 0.2s ease-in-out;
      box-shadow: 0 0 7px rgba(0, 0, 0, 0.3);
      &.open {
        left: 0px;
      }
    }
    #menu,
    #footer {
      width: 70px;
      &.open {
        width: 300px;
      }
      transition: width 0.2s ease-in-out;
      pointer-events: initial;
    }
    #menu {
      overflow-x: hidden;
      overflow-y: scroll;
      scrollbar-width: none;
      justify-content: flex-start;
      flex: 3;
      scroll-behavior: smooth;
      min-height: 60px;
    }
    #footer {
      flex: 1;
      min-height: 240px;
      justify-content: flex-end;
      padding-bottom: 16px;
      margin-top: 12px;
      z-index: 1;
      overflow: hidden;
      menu-item:first-of-type {
        border-top: 3px solid var(--ripe-app-border-line, #2f2c56);
      }
    }
    #tooltip {
      position: absolute;
      z-index: 2;
      left: 76px;
      border-radius: 6px;
      height: 36px;
      margin-top: -49px;
      padding: 0 12px 0 6px;
      color: var(--ripe-app-sidebar-menuitem, #c0c0c0);
      display: flex;
      justify-content: center;
      align-items: center;
      text-rendering: geometricPrecision;
      text-align: left;
      white-space: nowrap;
      font-family: var(--ripe-app-font-family), Helvetica, Arial, Sans-serif;
      background-color: var(--ripe-app-nav-rail-tooltip-bg, #363738);
      color: var(--ripe-app-nav-rail-tooltip-text, #ffffff);
      font-size: 0.75rem;
      font-weight: 200;
      // this is the bg color of the rectangular part of the arrow
      border: 2px solid var(--ripe-app-nav-rail-tooltip-border, #363738);
      &.active {
        color: var(--ripe-app-sidebar-active-menuitem, #ffffff);
      }
    }
    #tooltip:after,
    #tooltip:before {
      right: calc(100% - 2px);
      top: 50%;
      border: solid transparent;
      content: ' ';
      height: 0;
      width: 0;
      position: absolute;
      pointer-events: none;
    }
    // This the border of the triangle of the arrow
    #tooltip:before {
      border-color: transparent;
      // this is the color of the triangle part of the arrow
      border-right-color: var(--ripe-app-nav-rail-tooltip-border, #363738);
      border-width: 21px;
      border-radius: 6px;
      margin-top: -21px;
    }
    // This defines the background of the triangle of the arrow
    #tooltip:after {
      // border-color: #080037;
      border-color: transparent;
      // this is the actual bg color of the triangle
      border-right-color: var(--ripe-app-nav-rail-tooltip-bg, #363738);
      border-width: 18px;
      margin-top: -18px;
      border-radius: 0;
      border-width: 18px;
    }
  }

  // @media only screen and (max-width: 700px) {
  //   #sidemenu {
  //     top: 40px;
  //     height: 100vh;
  //     height: calc(var(--vh, 1vh) * 100 - 40px);
  //     #backdrop {
  //       top: 40px;
  //       height: calc(var(--vh, 1vh) * 100 - 40px);
  //     }
  //   }
  // }
  // @media only screen and (max-width: 700px) and (display-mode: standalone) {
  //   #sidemenu {
  //     top: 80px;
  //     height: 100vh;
  //     height: calc(var(--vh, 1vh) * 100 - 80px);
  //     #backdrop {
  //       top: 80px;
  //       height: calc(var(--vh, 1vh) * 100 - 40px);
  //     }
  //   }
  // }
</style>
